import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle, ArrowRight, ArrowLeft, Sparkles, Target, Rocket } from 'lucide-react'
import toast from 'react-hot-toast'
import { authAPI } from '../services/api'

const OnboardingPage = () => {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(0)
  const [formData, setFormData] = useState({
    companyName: '',
    industry: '',
    teamSize: '',
    useCase: '',
    goals: []
  })

  const steps = [
    {
      id: 'welcome',
      title: 'Welcome to HelplineAgent! 🎉',
      description: 'Let\'s get you set up in just a few steps',
      icon: Sparkles
    },
    {
      id: 'company',
      title: 'Tell us about your company',
      description: 'This helps us personalize your experience',
      icon: Target
    },
    {
      id: 'goals',
      title: 'What are your goals?',
      description: 'Select all that apply',
      icon: Rocket
    }
  ]

  const industries = [
    'E-commerce', 'SaaS', 'Healthcare', 'Education', 'Finance',
    'Real Estate', 'Professional Services', 'Other'
  ]

  const teamSizes = [
    '1-10', '11-50', '51-200', '201-500', '500+'
  ]

  const useCases = [
    'Customer Support', 'Lead Generation', 'Appointment Scheduling',
    'Order Tracking', 'FAQ Automation', 'Other'
  ]

  const goalOptions = [
    'Reduce response time',
    'Handle more inquiries',
    'Improve customer satisfaction',
    'Reduce support costs',
    'Scale support operations',
    '24/7 availability'
  ]

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
      
      // Update onboarding progress
      try {
        await authAPI.updateOnboarding({
          step: steps[currentStep + 1].id,
          completed: false
        })
      } catch (error) {
        console.error('Failed to update onboarding progress:', error)
      }
    } else {
      // Complete onboarding
      try {
        await authAPI.updateOnboarding({
          step: 'completed',
          completed: true
        })
        toast.success('Setup complete! Welcome to HelplineAgent 🚀')
        navigate('/dashboard')
      } catch (error) {
        console.error('Failed to complete onboarding:', error)
        toast.error('Failed to complete setup')
      }
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSkip = async () => {
    try {
      await authAPI.updateOnboarding({
        step: 'skipped',
        completed: true
      })
      navigate('/dashboard')
    } catch (error) {
      console.error('Failed to skip onboarding:', error)
    }
  }

  const toggleGoal = (goal) => {
    setFormData(prev => ({
      ...prev,
      goals: prev.goals.includes(goal)
        ? prev.goals.filter(g => g !== goal)
        : [...prev.goals, goal]
    }))
  }

  const isStepValid = () => {
    switch (currentStep) {
      case 0:
        return true
      case 1:
        return formData.companyName && formData.industry && formData.teamSize && formData.useCase
      case 2:
        return formData.goals.length > 0
      default:
        return false
    }
  }

  const StepIcon = steps[currentStep].icon

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center px-4 py-8">
      <div className="max-w-2xl w-full">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">
              Step {currentStep + 1} of {steps.length}
            </span>
            <button
              onClick={handleSkip}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Skip for now
            </button>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          {/* Step Icon & Title */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <StepIcon className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {steps[currentStep].title}
            </h1>
            <p className="text-gray-600">
              {steps[currentStep].description}
            </p>
          </div>

          {/* Step Content */}
          <div className="space-y-6">
            {currentStep === 0 && (
              <div className="text-center space-y-4">
                <p className="text-lg text-gray-700">
                  You're just a few steps away from transforming your customer support with AI-powered agents.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="text-3xl mb-2">⚡</div>
                    <div className="font-semibold text-gray-900">Quick Setup</div>
                    <div className="text-sm text-gray-600">2 minutes to get started</div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="text-3xl mb-2">🤖</div>
                    <div className="font-semibold text-gray-900">AI Powered</div>
                    <div className="text-sm text-gray-600">Smart automation</div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="text-3xl mb-2">📈</div>
                    <div className="font-semibold text-gray-900">Grow Fast</div>
                    <div className="text-sm text-gray-600">Scale effortlessly</div>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    className="w-full h-11 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="Acme Inc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Industry *
                  </label>
                  <select
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    className="w-full h-11 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  >
                    <option value="">Select industry</option>
                    {industries.map(industry => (
                      <option key={industry} value={industry}>{industry}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team Size *
                  </label>
                  <select
                    value={formData.teamSize}
                    onChange={(e) => setFormData({ ...formData, teamSize: e.target.value })}
                    className="w-full h-11 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  >
                    <option value="">Select team size</option>
                    {teamSizes.map(size => (
                      <option key={size} value={size}>{size} employees</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Use Case *
                  </label>
                  <select
                    value={formData.useCase}
                    onChange={(e) => setFormData({ ...formData, useCase: e.target.value })}
                    className="w-full h-11 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  >
                    <option value="">Select use case</option>
                    {useCases.map(useCase => (
                      <option key={useCase} value={useCase}>{useCase}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-4">
                <p className="text-gray-600 mb-4">Select all goals that matter to you:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {goalOptions.map(goal => (
                    <button
                      key={goal}
                      onClick={() => toggleGoal(goal)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        formData.goals.includes(goal)
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          formData.goals.includes(goal)
                            ? 'border-blue-600 bg-blue-600'
                            : 'border-gray-300'
                        }`}>
                          {formData.goals.includes(goal) && (
                            <CheckCircle className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <span className="font-medium text-gray-900">{goal}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={handleBack}
              disabled={currentStep === 0}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                currentStep === 0
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>

            <button
              onClick={handleNext}
              disabled={!isStepValid()}
              className={`flex items-center gap-2 px-8 py-3 rounded-lg font-medium transition-all ${
                isStepValid()
                  ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {currentStep === steps.length - 1 ? 'Complete Setup' : 'Continue'}
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OnboardingPage